// Welche Farben an einem Ort des Feldes gelten — als **reine Funktion**.
//
// Vorher rechnete das der Zeichenpfad nebenbei: Zonenindex aus dem
// Kameraabstand, Nachbarzone, Mischfaktor ueber die letzten Meter vor der
// Grenze, dann zwei Himmelfarben mischen. Das stand mitten in der
// Bildaktualisierung, galt fuer den ganzen Bildschirm und war ohne Browser
// nicht pruefbar.
//
// Der Gewinn liegt danach: derselbe Verlauf wird an **zwei** Stellen gebraucht
// — im gerechneten Bild und im Rueckfall fuer Geraete ohne WebGL. Zwei
// Fassungen davon liefen still auseinander, und der Unterschied waere eine
// Farbkante, die nur auf manchen Geraeten auftaucht.
//
// Diese Datei kennt kein Phaser und keinen Zustand. Sie liest ihre Farben aus
// `balancing()` (Gate FG-31) und schreibt in einen **uebergebenen** Puffer, denn
// sie laeuft je Bild (Gate FG-14).
//
// Sie liegt neben `palette.ts` und nicht darin: `palette.ts` ist eine Tabelle
// von Konstanten fuer die vier Knotenzustaende, das hier ist eine Rechnung ueber
// die Zonentabelle.

import { clamp, hexToInt, mixHex } from '../core/math';
import { balancing, zoneAtDist, zonesFor } from '../data/balancing';

/**
 * Breite des Uebergangs vor einer Zonengrenze, in Weltmetern.
 *
 * **Ein benannter Wert an einer Stelle.** Vorher stand die Zahl zweimal in
 * derselben Zeile des Zeichenpfads; wer sie dort aendert, aendert sie halb.
 */
export const ZONE_BLEND = 500;

/**
 * Groesse des Zonenfensters, das der Shader bekommt.
 *
 * Vier aufeinanderfolgende Zonen decken zwei Grenzen ab — mehr passen bei
 * ganzzahligem Zoom nie gleichzeitig ins Bild. Die ganze Tabelle zu uebergeben
 * waere ein Uniform-Feld, das mit jeder neuen Zone waechst.
 */
export const ZONE_WINDOW = 4;

/** Die Farben, die an einem Ort gelten. Wird in Ort gefuellt (Gate FG-14). */
export interface ZoneColors {
  /** Index der Zone, in der der Ort liegt. */
  index: number;
  /** Die beiden Himmelfarben, bereits ueber die Grenze gemischt. */
  sky0: number;
  sky1: number;
  /** Der Hintergrund: das Mittel der beiden. */
  sky: number;
  /** Sternton — ebenfalls gemischt. */
  star: number;
  /** Nebelton — ebenfalls gemischt. */
  nebula: number;
  /** Anteil der naechsten Zone, 0…1. */
  blend: number;
}

/** Ein leerer Puffer. Einmal anlegen, nicht je Bild. */
export function makeZoneColors(): ZoneColors {
  return { index: 0, sky0: 0, sky1: 0, sky: 0, star: 0, nebula: 0, blend: 0 };
}

/**
 * Die Farben an einer Weltposition.
 *
 * Der Uebergang liegt in den letzten `ZONE_BLEND` Metern **vor** der Grenze:
 * was du dort siehst, ist bereits die naechste Zone. Jenseits der letzten Zone
 * gibt es keinen Nachbarn mehr — dann gilt sie unvermischt weiter, denn das
 * Feld ist endlos.
 */
export function zoneColorsAt(
  x: number,
  y: number,
  out: ZoneColors,
  era: number = balancing().eraFirst,
): ZoneColors {
  return zoneColorsAtDist(Math.hypot(x, y), out, era);
}

/**
 * Dieselbe Rechnung, wenn der Abstand schon vorliegt.
 *
 * Gerechnet wird als **laufende Mischung** ueber die Zonen statt als Griff auf
 * „diese und die naechste": das Ergebnis ist dasselbe, aber die Formel ist
 * genau die, die der Hintergrund-Shader je Bildpunkt ausfuehrt. Zwei Formen
 * derselben Rechnung waeren eine Farbkante, die nur auf manchen Geraeten
 * auftaucht.
 */
export function zoneColorsAtDist(
  dist: number,
  out: ZoneColors,
  era: number = balancing().eraFirst,
): ZoneColors {
  const zones = zonesFor(era);
  out.index = zoneAtDist(dist, era);

  let sky0 = hexToInt(zones[0].sky[0]);
  let sky1 = hexToInt(zones[0].sky[1]);
  let star = hexToInt(zones[0].star);
  let nebula = hexToInt(zones[0].nebula);
  for (let i = 1; i < zones.length; i++) {
    const t = zoneBlend(dist, zones[i].from);
    // Die Grenzen steigen — ab dem ersten Anteil 0 bleibt alles Weitere 0.
    if (t <= 0) break;
    sky0 = mixHex(sky0, hexToInt(zones[i].sky[0]), t);
    sky1 = mixHex(sky1, hexToInt(zones[i].sky[1]), t);
    star = mixHex(star, hexToInt(zones[i].star), t);
    nebula = mixHex(nebula, hexToInt(zones[i].nebula), t);
  }

  out.sky0 = sky0;
  out.sky1 = sky1;
  out.sky = mixHex(sky0, sky1, 0.5);
  out.star = star;
  out.nebula = nebula;
  // Der Anteil der **naechsten** Grenze, nicht der zuletzt vollen: nur er
  // beschreibt, wie weit der laufende Uebergang ist.
  out.blend = out.index + 1 < zones.length ? zoneBlend(dist, zones[out.index + 1].from) : 0;
  return out;
}

/** Anteil der Zone, die bei `from` beginnt — 0 davor, 1 ab der Grenze. */
export function zoneBlend(dist: number, from: number): number {
  return clamp((dist - (from - ZONE_BLEND)) / ZONE_BLEND, 0, 1);
}

/**
 * Das Zonenfenster fuer den Shader: `ZONE_WINDOW` aufeinanderfolgende Zonen um
 * den Ort herum, als flache Zahlenfelder.
 *
 * Die Felder werden **ueberschrieben**, nicht neu angelegt (Gate FG-14). Liegt
 * der Ort am Rand der Tabelle, wird das Fenster geklemmt; die laufende
 * Mischung liefert dann dieselben Farben, weil die Anteile davor 1 und danach
 * 0 sind.
 */
export interface ZoneWindow {
  from: Float32Array;
  /** Der **gemittelte** Himmel — mehr hat der Hintergrund nie gebraucht. */
  sky: Float32Array;
  star: Float32Array;
  nebula: Float32Array;
}

export function makeZoneWindow(): ZoneWindow {
  return {
    from: new Float32Array(ZONE_WINDOW),
    sky: new Float32Array(ZONE_WINDOW * 3),
    star: new Float32Array(ZONE_WINDOW * 3),
    nebula: new Float32Array(ZONE_WINDOW * 3),
  };
}

export function zoneWindowAt(
  x: number,
  y: number,
  out: ZoneWindow,
  era: number = balancing().eraFirst,
): ZoneWindow {
  const zones = zonesFor(era);
  const mitte = zoneAtDist(Math.hypot(x, y), era);
  const start = clamp(mitte - 1, 0, Math.max(0, zones.length - ZONE_WINDOW));
  for (let slot = 0; slot < ZONE_WINDOW; slot++) {
    const zone = zones[Math.min(start + slot, zones.length - 1)];
    // Ein geklemmter Platz wiederholt die letzte Zone. Ihre Grenze liegt dann
    // hinter dem Ort, der Anteil ist 1, und die Mischung mit sich selbst
    // aendert nichts.
    out.from[slot] = zone.from;
    // Die beiden Himmelfarben werden **hier** gemittelt, nicht je Bildpunkt:
    // ihr Mittel ist alles, was der Hintergrund je benutzt hat. Das Mitteln
    // vor dem Mischen ist dasselbe wie danach — beides ist linear.
    writeVec(out.sky, slot, mixHex(hexToInt(zone.sky[0]), hexToInt(zone.sky[1]), 0.5));
    writeVec(out.star, slot, hexToInt(zone.star));
    writeVec(out.nebula, slot, hexToInt(zone.nebula));
  }
  return out;
}

function writeVec(target: Float32Array, slot: number, value: number): void {
  target[slot * 3] = ((value >> 16) & 255) / 255;
  target[slot * 3 + 1] = ((value >> 8) & 255) / 255;
  target[slot * 3 + 2] = (value & 255) / 255;
}
